import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { AppContext, ModalsContext } from './contexts/AppContext';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

// Utils.js
class Utils  {

    static appContextValue = null;
    static modalsContextValue = null;
    static login_promise = null;
    static login_timeout = 20000; // meddig várhat a beléptetésre a többi API hívás (ms)

    static setAppContext(value) {
        Utils.appContextValue = value;
    }

    static setModalsContext(value) {
        Utils.modalsContextValue = value;
    }

    static getAppContextValue() {
        return (Utils.appContextValue ?? AppContext?._currentValue ?? AppContext?._currentValue2 ?? null);
    }

    static getModalsContextValue() {
        return (Utils.modalsContextValue ?? ModalsContext?._currentValue ?? ModalsContext?._currentValue2 ?? null);
    }

    static timestampToLocalDate(timestamp) {
        if (timestamp === null || timestamp === undefined) {
            return '';
        }

        const numeric = Number(timestamp);

        if (!Number.isFinite(numeric)) {
            return '';
        }

        const millis = numeric > 1e12 ? numeric : numeric * 1000;
        const dateInstance = new Date(millis);

        if (Number.isNaN(dateInstance.getTime())) {
            return '';
        }

        return dateInstance.toLocaleDateString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }

    static get_path_from_url(){

        const location = window.location.href.replace(process.env.REACT_APP_PUBLIC_URL,'');
        const location_array = location.split('/');

        if(location_array.length>0)
        {
            return location_array[0];
        }
        else
        {
            return '';
        }

    }

    static format_currency(value,currency_settings)
    {
        const currency_sign = currency_settings.sign ? currency_settings.sign : 'Ft';
        const decimal_places = currency_settings.decimals ? currency_settings.decimals : 0;
        const decimal_separator = currency_settings.decimal_separator ? currency_settings.decimal_separator : ',';
        const thousands_separator = currency_settings.thousands_separator ? currency_settings.thousands_separator : ' ';

        let parts = parseFloat(value).toFixed(decimal_places).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands_separator);

        return parts.join(decimal_separator) + ' ' + currency_sign;
    }

    /**
     * HTML entity decode - pl. &#039; -> '
     */
    static htmlDecode(str) {
        if (!str) return str;
        const doc = new DOMParser().parseFromString(str, 'text/html');
        return doc.documentElement.textContent;
    }

    /**
     * Az admin editorból jövő tartalmi HTML apphoz igazítása:
     * - a webshop saját domainjére mutató linkek relatívvá tétele (a Layout link-kezelője csak így tud route-ot csinálni belőlük)
     * - a webshophoz relatív képek/fájlok kiegészítése a webshop URL-jével
     */
    static normalize_content_html(html) {
        if (!html) return html;

        const shop_url = (process.env.REACT_APP_ORIGINAL_SHOP_URL || '').replace(/\/+$/, '');

        if (!shop_url) return html;

        const shop_host = shop_url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        // csak akkor vágjuk le a domaint, ha útvonal is követi (a puszta domainből üres href lenne)
        let result = html.replace(new RegExp('(href=")https?://(?:www\\.)?' + shop_host + '/(?=[^"])', 'gi'), '$1');

        // relatív src (nem //, nem http, nem data:) a webshopra mutat
        result = result.replace(/(<(?:img|source|video)\b[^>]*?\bsrc=")(?!https?:|\/|data:)/gi, '$1' + shop_url + '/');

        return result;
    }

    /**
     * Tartalom (content) oldal megnyitása modal ablakban, navigáció helyett.
     * Így a formon már beírt adat nem vész el (pl. ÁSZF a checkbox címkéjében).
     *
     * @param {string} url      a tartalom oldal url-je (relatív, vagy a saját domainre mutató abszolút)
     * @param {object} options  fallback: ha a szerver nem ad vissza tartalmat, ez fut le helyette (pl. sima navigáció)
     */
    static open_content_modal(url, options = {}) {

        const fallback = typeof options.fallback === 'function' ? options.fallback : null;
        const shop_url = (process.env.REACT_APP_ORIGINAL_SHOP_URL || '').replace(/^['"]|['"]$/g, '').replace(/\/+$/, '');

        let path = (url || '').trim();

        // az appban a window.location.origin nem a shop domainje, ezért a shop URL-jét is le kell vágni
        [window.location.origin, shop_url].forEach(base => {
            if (base) path = path.replace(base + '/', '');
        });

        path = path.replace(/^\/+/, '');

        if (!path.length) {
            if (fallback) fallback();
            return;
        }

        // a tartalom oldal ugyanazon a szerződésen kérhető le, mint a Content modulban: content=1
        const [clean_path, search] = path.split('?');
        const query_string = {content: '1', APICACHE: 'false'};

        if (search) {
            new URLSearchParams(search).forEach((value, key) => { query_string[key] = value; });
        }

        Utils.get_server_data(
            '/content/' + clean_path,
            query_string,
            {}
        ).then(res => {
            const modalsContext = Utils.getModalsContextValue();

            // csak akkor nyitunk modalt, ha tényleg tartalom van mögötte, egyébként marad az eredeti viselkedés
            if (res && (!!res.content || !!res.title) && modalsContext?.PopupContentRef?.current) {
                modalsContext.PopupContentRef.current.show(res);
            }
            else if (fallback) {
                fallback();
            }
        });
    }

    /**
     * Modalban kell-e megnyitni a kattintott linket?
     * - explicit jelölés: data-type="modal" vagy data-popup="1"
     * - formon belüli belső link: hogy a beírt adat ne vesszen el (kivéve ha a form data-modal-links="0")
     */
    static is_modal_link(linkElement) {

        if (!linkElement) return false;

        if (linkElement.dataset.type === 'modal' || parseInt(linkElement.getAttribute('data-popup')) === 1) {
            return true;
        }

        const form = linkElement.closest ? linkElement.closest('form, [data-modal-links]') : null;

        return !!form && form.dataset.modalLinks !== '0' && !linkElement.dataset.type;
    }

    /**
     * A --vh CSS változó szinkronizálása a tényleges ablakmagassággal (iOS vh bug).
     * Az App is hívja előtérbe kerüléskor, amikor elmaradhattak a resize események.
     */
    static sync_viewport() {
        const vh = window.innerHeight;

        // a WebView felfüggesztésekor/visszatéréskor átmenetileg 0 vagy irreálisan kicsi magasság is jöhet,
        // azzal az egész alkalmazás-konténer összeomlana, és semmi nem állítaná vissza
        if (!vh || vh < 200) return;

        document.documentElement.style.setProperty('--vh', `${vh}px`);
    }

    static openExternalUrl(url, payload = {}) {
        if (!url || typeof url !== 'string') {
            return;
        }

        if (window.NativeAndroid?.openExternalUrl) {
            window.NativeAndroid.openExternalUrl(url);
            return;
        }

        // Capacitor natív platformon: Browser plugin nyitja meg rendszer-böngészőben (Safari)
        try {
            const { Browser } = require('@capacitor/browser');
            const { Capacitor } = require('@capacitor/core');
            if (Capacitor.isNativePlatform()) {
                Browser.open({ url });
                return;
            }
        } catch (e) {
            // Browser plugin nem elérhető, fallback
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    }

    static openPhoneNumber(phone) {
        const cleanPhone = String(phone ?? '').replace(/[^+\d]/g, '');

        if (!cleanPhone) {
            return;
        }

        Utils.openExternalUrl(`tel:${cleanPhone}`, {
            phoneNumber: cleanPhone,
        });
    }

    static openEmailAddress(email) {
        const cleanEmail = String(email ?? '').trim();

        if (!cleanEmail) {
            return;
        }

        Utils.openExternalUrl(`mailto:${cleanEmail}`, {
            email: cleanEmail,
        });
    }

    /**
     * Product label render - ellenőrzi az időpontokat és rendereli a badge-t
     * @param {Object} product - A termékobjektum
     * @param {string} location - 'product_list_item' vagy 'product_detail'
     * @returns {JSX|null}
     */
    static getProductLabelElement(product, location = 'product_list_item') {
        if (!product?.products_label) {
            return null;
        }

        const label = product.products_label;
        const image = location === 'product_list_item' ? label.label_img_thumb : label.label_img;

        if (!image) {
            return null;
        }

        const now = new Date();
        const startTimestamp = label.pp_start_time ? Number(label.pp_start_time) : null;
        const endTimestamp = label.pp_end_time ? Number(label.pp_end_time) : null;
        const startDate = startTimestamp ? new Date(startTimestamp * 1000) : null;
        const endDate = endTimestamp ? new Date(endTimestamp * 1000) : null;

        // Ha a label még nem kezdődött el
        if (startDate && startDate > now) {
            return null;
        }

        // Ha a label már lejárt
        if (endDate && endDate < now) {
            return null;
        }

        return {
            image,
            productParameter: label.product_parameter
        };
    }

    /**
     * Meghívja az API végpontot, úgy hogy a linkből előbb kiveszi az első tagot (ha van), mert az a react route megnevezése.
     */
    static async get_server_data(path = '', query_string = {}, post_data = {}) {

        let result = {};

        const location = path;
        const location_array = location.split('/');
        const remember_hash = this.localSorageGetWithExpiry('remember_hash_hu');
        const appContext = Utils.getAppContextValue();

        if(remember_hash){
            query_string['device_id'] = remember_hash;
        }

         // automatikus beléptetés ha van remember_hash localstorage-ban
        if (remember_hash && appContext?.appRef?.current && !appContext?.appRef?.current?.state?.is_signed && query_string.ajax_event !== 'login')
        {
            if (!Utils.login_promise) {
                Utils.login_promise = new Promise((resolve) => {

                    // ha a beléptetés valamiért nem fejeződik be (elhalt kérés, hiba), akkor sem várakoztathatjuk
                    // örökre az összes további API hívást - a login_promise statikus, az app bezárásáig élne
                    const timeout = setTimeout(resolve, Utils.login_timeout);

                    const finish = (res) => {
                        clearTimeout(timeout);
                        resolve(res);
                    };

                    appContext?.appRef?.current?.login(true, null, remember_hash, finish, (res) => {
                        // csak akkor felejtjük el a belépési hash-t, ha a szerver tényleg elutasította:
                        // egy hálózati hiba miatt ne léptessük ki némán a felhasználót
                        if (!res?.error_request) {
                            localStorage.removeItem('remember_hash_hu');
                        }

                        finish(res);
                    });
                }).catch(() => {}).finally(() => {
                    Utils.login_promise = null;
                });
            }

            // ha php oldalon még nem nincs bejelentkezve
            await Utils.login_promise;
        }

        const target_path = location_array
            .filter((value, index) => index > 1)
            .join('/') + ((Object.keys(query_string).length>0) ? '?' + this.objectToQueryString(query_string) : '');

        const options = {};

        if (Object.keys(post_data).length>0) {
            options.method = "POST";

            const hasFile = (data) => {
                if (data instanceof File || data instanceof Blob) return true;
                if (data && typeof data === 'object') {
                    return Object.keys(data).some(key => hasFile(data[key]));
                }
                return false;
            };

            if (hasFile(post_data)) {
                const formData = new FormData();
                const appendFD = (data, pKey) => {
                    if (data && typeof data === 'object' && !(data instanceof File) && !(data instanceof Blob)) {
                        Object.keys(data).forEach(k => {
                            appendFD(data[k], pKey ? `${pKey}[${k}]` : k);
                        });
                    } else {
                        formData.append(pKey, data == null ? '' : data);
                    }
                };
                appendFD(post_data, '');
                options.body = formData;
            } else {
                options.body = this.objectToQueryString(post_data);
                options.headers = { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' };
            }
        }

        const url = process.env.REACT_APP_API_SERVER_URL + target_path;

        result = await this.xhr_fetch(url, options).then((res) => { 
            if(res && res.dialog_message){
                
                appContext?.appRef?.current?.set_dialog_message(res.dialog_message);
            }
            return res; 
        })
    
        return result;
    }

    // ha egy a tag-nek üres a data-type attribútuma akkor le kell kérdezni a szervertől a link adatait,
    // és a válasz alapján lehet tovább navigálni
    static async get_url_data(url) {
        let result = {};

        if (url !== null) {
            url = url.replace(window.location.origin + '/', '');

            // a szerver a rewrite URL-t query string nélkül keresi, ezért csak az útvonalat küldjük
            const path = url.split('#')[0].split('?')[0];

            if (!!path && !!path.length) {

                const options = {};
                options.method = "GET";
                options.mode = "cors";
                options.headers = { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' };

                result = await Utils.xhr_fetch(process.env.REACT_APP_API_SERVER_URL + '?ajax_event=get_url_data&url='+encodeURIComponent(path)+'&APICACHE=false', options).then((res) => { return res; })
            }
        }

        return result;
    }

    static setCookie(cname, cvalue, exdays) {
        var expires = '';

        if(exdays)
        {
            const d = new Date();
            d.setTime(d.getTime() + (exdays*24*60*60*1000));
            expires = "expires="+ d.toUTCString();
        }

        document.cookie = cname + "=" + cvalue + ";" + expires + ";domain=" + process.env.REACT_APP_COOKIE_DOMAIN + ";path=/";
      }

    static xhr_fetch(url, options = {}) {

        var default_options = {
            mode: 'cors',
            method: 'GET',
            credentials: 'include'
        };

        options = Object.assign(default_options, options);
     
        if(!options.headers) {
            options.headers = {};

        }

        return window.fetch(url, options)
            .then(res => {
                return res.json();
            })
            .then((res) => {
                return res;
            })
            .catch((msg) => {
                // hálózati hiba vagy nem JSON válasz (pl. hibaoldal): jelzett hibaobjektumot adunk vissza,
                // hogy a hívók ne undefined-on szálljanak el (az így eldobott kivétel korábban beragasztotta a beléptetést)
                console.error('xhr_fetch', url, msg);

                return {error_request: true};
            });
    }


    static objectToQueryString(obj, prefix) {
        const str = [];
        for (const p in obj) {
            if (obj.hasOwnProperty(p)) {
                const k = prefix ? `${prefix}[${p}]` : p;
                const v = obj[p];
                if (v !== null && typeof v === "object" && !(v instanceof File) && !(v instanceof Blob)) {
                    str.push(Utils.objectToQueryString(v, k));
                } else {
                    str.push(encodeURIComponent(k) + "=" + encodeURIComponent(v == null ? "" : v));
                }
            }
        }
        return str.join("&");
    }

    static localSorageSetWithExpiry(key, value, ttl) {
        try {
            const now = new Date()

            const item = {
                value: value,
                expiry: now.getTime() + ttl,
            }

            localStorage.setItem(key, JSON.stringify(item))
        } catch (e) {
            // iOS Safari private mode vagy quota túllépés esetén silent fail
        }
    }

    static localSorageGetWithExpiry(key) {
        try {
            const itemStr = localStorage.getItem(key)

            // if the item doesn't exist, return null
            if (!itemStr) {
                return null
            }

            const item = JSON.parse(itemStr)
            const now = new Date()

            // compare the expiry time of the item with the current time
            if (now.getTime() > item.expiry) {
                // If the item is expired, delete the item from storage
                // and return null
                localStorage.removeItem(key)
                return null
            }

            return item.value
        } catch (e) {
            // iOS Safari private mode vagy sérült adat esetén silent fail
            return null
        }
    }

    static add_product_to_shopping_cart(product_id,pc,item_attributes, imageElement = null, extraData = null){

        pc = (pc ? pc : 1);
        item_attributes = (item_attributes ? item_attributes : '');

        // Ha van kép elem, indítjuk az animációt
        if(imageElement){
            this.animateProductToCart(imageElement);
        }

        const post_data = {
            'ajax_event':'get_shoppingcart',
            'ajax_add_to_cart':1,
            'pc_product': pc,
            'sql_id_product': product_id,
            'item_attributes_product': item_attributes,
            'webshop_event': 'add_shopping_cart',
            ...(extraData || {})
        };

        this.get_update_shopping_cart(post_data);
    }

    static animateProductToCart(sourceElement){
        
        if(!sourceElement) return;

        // Megkeressük a minicart ikont
        const minicartIcon = document.querySelector('.minicart-icon');
        if(!minicartIcon) return;

        // Klónozzuk a forrás elemet
        const clone = sourceElement.cloneNode(true);
        const sourceRect = sourceElement.getBoundingClientRect();
        const targetRect = minicartIcon.getBoundingClientRect();

        // Beállítjuk a klón kezdő pozícióját és stílusát
        clone.style.position = 'fixed';
        clone.style.top = `${sourceRect.top}px`;
        clone.style.left = `${sourceRect.left}px`;
        clone.style.width = `${sourceRect.width}px`;
        clone.style.height = `${sourceRect.height}px`;
        clone.style.zIndex = '9999';
        clone.style.pointerEvents = 'none';
        clone.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        clone.classList.add('flying-product-animation');

        // Hozzáadjuk a DOM-hoz
        document.body.appendChild(clone);

        // Kis késleltetés után indítjuk az animációt
        setTimeout(() => {
            clone.style.top = `${targetRect.top + targetRect.height/2}px`;
            clone.style.left = `${targetRect.left + targetRect.width/2}px`;
            clone.style.width = '20px';
            clone.style.height = '20px';
            clone.style.opacity = '0';
            clone.style.transform = 'scale(0.1)';
        }, 10);

        // Animáció után töröljük a klónt
        setTimeout(() => {
            if(clone && clone.parentNode){
                clone.parentNode.removeChild(clone);
            }
        }, 850);
    }

    static shopping_cart_modify(product,order_id,coupon_code,loyalti_data)
    {
        let post_data = {};

        post_data['shopping_cart_pc_'+product.orders_items_id+'_x'] = product.pc;
        post_data['sql_id_orders_items_'+product.orders_items_id+'_x'] = product.orders_items_id;
        post_data['sql_id_orders_'+product.orders_items_id+'_x'] = order_id;
        post_data['shopping_cart_modify'] = 'shopping_cart_modify_'+product.orders_items_id+'_x';

        if(coupon_code){
            post_data['select_coupon'] = coupon_code;
        }

        if(loyalti_data){
            post_data = this.shopping_cart_set_loyalti_codes(post_data,loyalti_data);
        }


        this.get_update_shopping_cart(post_data);
    }

    static shopping_cart_delete(product,order_id,coupon_code,loyalti_data)
    {
        let post_data = {};

        post_data['shopping_cart_pc_'+product.orders_items_id+'_x'] = product.pc;
        post_data['sql_id_orders_items_'+product.orders_items_id+'_x'] = product.orders_items_id;
        post_data['sql_id_orders_'+product.orders_items_id+'_x'] = order_id;
        post_data['shopping_cart_delete'] = 'shopping_cart_delete_'+product.orders_items_id+'_x';

        if(coupon_code){
            post_data['select_coupon'] = coupon_code;
        }

        if(loyalti_data){
            post_data = this.shopping_cart_set_loyalti_codes(post_data,loyalti_data);
        }


        this.get_update_shopping_cart(post_data);
    }

    static shopping_cart_set_coupon(coupon_code)
    {
        const post_data = {};

        post_data['select_coupon'] = coupon_code;

        this.get_update_shopping_cart(post_data);
    }

    static shopping_cart_set_loyalti_codes(post_data,loyalti_data)
    {
        const _post_data = post_data ? post_data : {};

        if(loyalti_data?.loyalti_code)
        {
            _post_data['select_ugyfelkartya'] = loyalti_data.loyalti_code;
        }
        
        if(loyalti_data?.loyalti_gross)
        {
            _post_data['select_ugyfelkartya_gross'] = loyalti_data.loyalti_gross;
        }

        if(loyalti_data?.loyalti_coupon_id)
        {
            _post_data['select_ugyfelkartya_coupon_id'] = loyalti_data.loyalti_coupon_id;
        }
            
        return _post_data;
    }
    

    static get_update_shopping_cart(post_data,initial){

        const post_data_ = (post_data) ? post_data : {};

        post_data_['ajax_event'] = 'get_shoppingcart';

        this.get_server_data('',{},post_data_).then((resp) => {
            // ha üres a kosár akkor object helyett false jön vissza
            const appContext = Utils.getAppContextValue();
            if(resp && typeof resp.shopping_cart !== 'undefined'){
                appContext?.appRef?.current?.get_update_shopping_cart(resp.shopping_cart,initial);
            }

            //ha módosít a kosáron újra kell inicializálni a checkoutot 
            const modalsContext = Utils.getModalsContextValue();
            if(modalsContext?.CheckoutSummaryRef?.current){
                modalsContext.CheckoutSummaryRef.current.checkoutInitDone = false;
            }
        });

    }
}


function NavigateTo({toURL,prefix}) {

    const navigate = useNavigate();

    useEffect(() => {
        if(prefix){
        toURL = prefix+'/'+toURL;
        }
        navigate(toURL)
    },[]);


    return <></>;
}

function AnimatePresenceWrapper({ children }) {
    return <AnimatePresence>{children}</AnimatePresence>;
}

const ElementVisibilityChecker = (props) => {

    const [isVisible, setIsVisible] = useState(false);
    const targetRef = useRef(null);
    const children = (props.children) ? props.children : null;
  
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
          if(entry.isIntersecting && typeof props.onVisible == 'function'){
            props.onVisible(children);
          }
        },
        {
          root: null, // viewport
          rootMargin: '0px', // no margin
          threshold: 0.5, // 50% of target visible
        }
      );
  
      if (targetRef.current) {
        observer.observe(targetRef.current);
      }
  
      // Clean up the observer
      return () => {
        if (targetRef.current) {
          observer.unobserve(targetRef.current);
        }
      };
    }, []);
  
    return (
        <div ref={targetRef}>
            {children}
        </div>
    );
  };

  //ez az IOS css vh bug miatt kell
  const setVh = () => Utils.sync_viewport();

  setVh();

  if (window.visualViewport) {
    // --vh frissítése resize-kor
    window.visualViewport.addEventListener('resize', setVh);
    // iOS-en billentyűzet bezárásakor visszaállítjuk a scroll pozíciót
    window.visualViewport.addEventListener('resize', () => {
      const isKeyboardClosed = window.visualViewport.height >= (window.screen.height * 0.75);
      if (isKeyboardClosed) {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }
    });
  } else {
    window.addEventListener("resize", setVh);
  }

  // Capacitor Keyboard plugin - iOS billentyűzet hide esemény
  if (Capacitor.isNativePlatform()) {
    Keyboard.addListener('keyboardWillHide', () => {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        setVh();
      }, 50);
    });
  }

export {Utils, AnimatePresenceWrapper, NavigateTo, ElementVisibilityChecker };